import { AfterViewInit, Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { flatMap as _flatMap, get as _get } from 'lodash';
import { combineLatest, Subject } from 'rxjs';
import { filter, map, take, takeUntil } from 'rxjs/operators';

import { addVehicleBindQuery, addVehicleInfoQuery } from '@amfam/policy/add-vehicle/data-access';
import { MatDatePickerOptions } from '@amfam/shared/models';
import { userQuery } from '@amfam/shared/user';
import { BrandSelectors } from '@amfam/shared/utility/brand';
import { fromRouterActions } from '@amfam/shared/utility/navigation';
import { CopyService } from '@amfam/shared/utility/shared-services';
import { LoadingSpinnerService } from '@amfam/ui-kit';

import * as VehicleInfoActions from '../+state/add-vehicle-feature.actions';
import {
  setVehicleLeaseStartDate,
  setVehicleTPIsInfo
} from '../+state/add-vehicle-feature.actions';
import * as addVehicleFeatureSelector from '../+state/add-vehicle-feature.selectors';

@Component({
  selector: 'ds-vehicle-loan-info',
  templateUrl: './vehicle-loan-info.component.html',
  styleUrls: ['./vehicle-loan-info.component.scss'],
  standalone: false
})
export class VehicleLoanInfoComponent implements OnInit, AfterViewInit, OnDestroy {
  @Output() cancelEvent = new EventEmitter<boolean>();
  matDatePickerOptions: MatDatePickerOptions;

  isPartner$ = this.store.select(BrandSelectors.selectIsPartner);
  public tpisInfoForm: UntypedFormGroup;
  public userState$ = this.store.select(userQuery.selectUserStateCode);
  public canShowDatePicker: boolean;
  public headerContentPath: string;

  private tpiList$ = this.store.select(addVehicleInfoQuery.selectNewVehicleTPIs);
  // eslint-disable-next-line @typescript-eslint/member-ordering
  public tpiDescriptionList$ = this.tpiList$.pipe(
    map(tpiList => {
      const tpiDescriptionList = _flatMap(tpiList, tpiItem => ({
        text: tpiItem.name,
        id: tpiItem.publicId
      }));
      tpiDescriptionList.push({
        text: this.copyService.getCopy('policy.newVehicleTPINoneOfTheseText'),
        id: 'None'
      });
      return tpiDescriptionList;
    })
  );

  private stop$: Subject<void> = new Subject<void>();

  constructor(
    private store: Store,
    private formBuilder: UntypedFormBuilder,
    private copyService: CopyService,
    private spinnerService: LoadingSpinnerService
  ) {}

  ngOnInit(): void {
    this.matDatePickerOptions = {
      maxDate: new Date()
    };
    this.spinnerService.stop();
    this.buildForm();
    this.handleError();
  }

  ngAfterViewInit() {
    this.store.dispatch(
      VehicleInfoActions.addVehicleSendAnalytics({
        payload: { pageAnalyticName: 'Lease' }
      })
    );
  }

  submitTPIsInfo() {
    this.spinnerService.start({
      blockActions: true
    });

    if (this.canShowDatePicker) {
      this.store.dispatch(
        setVehicleLeaseStartDate({
          leaseStartDate: _get(this.tpisInfoForm.controls['leaseDate'], 'value')
        })
      );
    }

    this.tpiList$.pipe(take(1)).subscribe(tpiList => {
      this.store.dispatch(
        setVehicleTPIsInfo({
          tpisInfo: tpiList.find(
            tpi => tpi.name === _get(this.tpisInfoForm.controls['tpiOption'], 'value.text')
          )
        })
      );

      this.store.dispatch(fromRouterActions.Go({ path: ['policies/add-vehicle/driver-info'] }));
    });
  }

  goBackOneStep() {
    this.store.dispatch(fromRouterActions.Back());
  }

  ngOnDestroy() {
    this.stop$.next();
    this.stop$.complete();
  }

  private buildForm() {
    combineLatest(
      this.store.select(addVehicleFeatureSelector.selectCurrentVehicleInfo),
      this.userState$,
      this.isPartner$
    )
      .pipe(
        filter(([currentVehicle, userState]) => !!currentVehicle && !!userState),
        take(1)
      )
      .subscribe(([vehicleDetailsState, userState, isPartner]) => {
        if (isPartner) {
          if (_get(vehicleDetailsState, 'underwritingQuestions.hasLease')) {
            const datePickerStates = ['NJ', 'NY', 'FL', 'MA'];
            if (datePickerStates.includes(userState)) {
              this.canShowDatePicker = true;
            }
            const date = new Date();
            this.headerContentPath = ['NJ', 'NY', 'FL', 'MA'].includes(userState as string)
              ? 'policy.newVehicleLeaseInstitutions'
              : 'policy.newVehicleLeaseInstitutionsExceptFewStates';
            this.tpisInfoForm = this.formBuilder.group({
              tpiOption: ['', Validators.compose([Validators.required])],
              leaseDate: ['', Validators.compose([Validators.required])]
            });
            this.tpisInfoForm.patchValue({
              leaseDate: {
                date: {
                  year: date.getFullYear(),
                  month: date.getMonth() + 1,
                  day: date.getDate()
                }
              }
            });
          } else {
            this.headerContentPath = 'policy.newVehicleLoanInstitutions';
            this.tpisInfoForm = this.formBuilder.group({
              tpiOption: ['', Validators.compose([Validators.required])]
            });
          }
        } else {
          this.headerContentPath = 'policy.newVehicleLoanLeaseInstitutions';
          this.tpisInfoForm = this.formBuilder.group({
            tpiOption: ['', Validators.compose([Validators.required])]
          });
        }
      });
  }

  private handleError() {
    combineLatest([
      this.store.select(addVehicleInfoQuery.selectNewVehicleInitiateError),
      this.store.select(addVehicleBindQuery.selectVehicleANSNotificationError)
    ])
      .pipe(
        map(([initiateError, ansError]) => ({ initiateError, ansError })),
        takeUntil(this.stop$)
      )
      .subscribe(({ initiateError, ansError }) => {
        if (initiateError || ansError) {
          this.spinnerService.stop();
          this.store.dispatch(
            fromRouterActions.Go({
              path: ['policies/add-vehicle/agent-help']
            })
          );
        }
      });
  }
}
